/**
 * Human Resources · calculators.
 *
 * Four questions HR is asked every week, answered with the Saudi labour
 * rules in force: the end-of-service award, the annual-leave balance and
 * what it is worth, what a stretch of sick leave pays, and the final
 * settlement when somebody leaves. Pick a person to fill the figures from
 * their file, or type any figures.
 *
 * Everyone is paid a monthly salary; every calculator works from the month.
 */
import { useMemo, useState } from "react";
import { useHrPeople, useHrRecords, useHrRules } from "../../api/queries";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { Card, Field, Input, Segmented, Select } from "../../design/components";
import { eosb, eosbWage, grossPay, mergeRules, type HrEmployee, type HrRules, type LeaveReason } from "../../domain/hr";
import { finalSettlement, serviceTo, sickLeave, sickPay, vacation, vacationOf } from "../../domain/hrFile";
import { t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import { useHrAccess } from "./access";
import { h, niceDate, PreviewBar, todayIso } from "./kit";
import { s, SampleBanner, sar, SUITE } from "./shared";

type Calc = "eosb" | "vacation" | "sick" | "final";
const CALCS: { value: Calc; label: string }[] = [
  { value: "eosb", label: "End of service" },
  { value: "vacation", label: "Vacation balance" },
  { value: "sick", label: "Sick-leave pay" },
  { value: "final", label: "Final settlement" },
];

export function HrCalculatorsPage() {
  const session = useSession();
  const [calc, setCalc] = useState<Calc>("eosb");
  const rules = useHrRules().data?.rules ?? mergeRules(null);
  const people = useHrPeople();
  const records = useHrRecords();
  const { can } = useHrAccess();
  const [who, setWho] = useState("");
  const e = people.data?.employees.find((x) => x.id === who) ?? null;
  const mine = useMemo(() => (records.data?.records ?? []).filter((r) => r.employeeId === who), [records.data, who]);

  useScreenContext({
    route: "/hr-calculators",
    title: "Calculators",
    summary: [
      t(
        "End of service, vacation balance, sick-leave pay and the final settlement, by the Saudi labour rules in force.",
      ),
    ],
    offers: [
      { label: t("How is end of service worked out?"), ask: "how is end of service calculated in Saudi Arabia?" },
    ],
    worksInFull: true,
  });

  return (
    <Page eyebrow={t("{site} · {suite}", { site: session.site.code, suite: t(SUITE) })} title="Calculators">
      <PreviewBar />
      <SampleBanner />
      <Card
        title={t("Whose figures?")}
        sub={t("Pick a person to fill from their file, or type your own figures.")}
      >
        <div className={s.form}>
          <Field label="Person">
            <Select value={who} onChange={(ev) => setWho(ev.target.value)} data-testid="hr-calc-person">
              <option value="">{t("Nobody — I will type the figures")}</option>
              {(people.data?.employees ?? [])
                .filter((x) => x.employment === "direct")
                .map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name} · {x.employeeNo}
                  </option>
                ))}
            </Select>
          </Field>
        </div>
        {e && !can.pay && <p className={s.note}>{t("Pay is hidden from you, so type the monthly wage yourself.")}</p>}
      </Card>
      <Segmented value={calc} options={CALCS} onChange={setCalc} ariaLabel="Calculator" />
      {calc === "eosb" && <EosbCalc key={`e${who}`} e={e} rules={rules} can={can.pay} />}
      {calc === "vacation" && <VacationCalc key={`v${who}`} e={e} records={mine} rules={rules} can={can.pay} />}
      {calc === "sick" && <SickCalc key={`s${who}`} e={e} records={mine} can={can.pay} />}
      {calc === "final" && <FinalCalc key={`f${who}`} e={e} records={mine} rules={rules} can={can.pay} />}
      <p className={s.note}>
        {t(
          "Rates as of {d} are proposed until HR confirms them. A day's pay is the monthly wage ÷ 30.",
          { d: rules.asOf },
        )}
      </p>
    </Page>
  );
}

const years = (e: HrEmployee | null) => (e ? serviceTo(e, new Date()).toFixed(2) : "5");

function Result({ rows, total, testId }: { rows: [string, string][]; total: [string, string]; testId: string }) {
  return (
    <dl className={h.facts} style={{ marginTop: 14 }}>
      {rows.map(([k, v]) => (
        <div key={k} style={{ display: "contents" }}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
      <dt>
        <b>{total[0]}</b>
      </dt>
      <dd>
        <b className={h.big} data-testid={testId}>
          {total[1]}
        </b>
      </dd>
    </dl>
  );
}

function EosbCalc({ e, rules, can }: { e: HrEmployee | null; rules: HrRules; can: boolean }) {
  const [wage, setWage] = useState(e && can ? String(eosbWage(e, rules)) : "10000");
  const [y, setY] = useState(years(e));
  const [reason, setReason] = useState<LeaveReason>("termination");
  const r = eosb(Number(wage) || 0, Number(y) || 0, reason, rules);
  return (
    <Card
      title={t("End-of-service award")}
      sub={t("Labor Law art. 84; reduced on resignation (art. 85).")}
      testId="hr-calc-eosb"
    >
      <div className={s.form}>
        <Field label="Monthly wage the award is paid on (SAR)">
          <Input
            value={wage}
            onChange={(ev) => setWage(ev.target.value)}
            inputMode="decimal"
            data-testid="hr-calc-wage"
          />
        </Field>
        <Field label="Years of service">
          <Input value={y} onChange={(ev) => setY(ev.target.value)} inputMode="decimal" data-testid="hr-calc-years" />
        </Field>
        <Field label="How the service ends">
          <Select
            value={reason}
            onChange={(ev) => setReason(ev.target.value as LeaveReason)}
            data-testid="hr-calc-reason"
          >
            <option value="termination">{t("Ended by the employer")}</option>
            <option value="contract_end">{t("Fixed-term contract ended")}</option>
            <option value="resignation">{t("Resignation")}</option>
          </Select>
        </Field>
      </div>
      <Result
        testId="hr-calc-eosb-amount"
        rows={[
          [
            t("First {y} years × {m} month's wage", { y: r.firstYears, m: rules.eosb.firstRateMonths }),
            sar(r.firstAmount),
          ],
          [
            t("Then {y} years × {m} month's wage", { y: r.laterYears, m: rules.eosb.laterRateMonths }),
            sar(r.laterAmount),
          ],
          [t("The full award (art. 84)"), sar(r.full)],
          ...(reason === "resignation"
            ? ([[t("Kept on resignation (art. 85)"), `${Math.round(r.factor * 100)}%`]] as [string, string][])
            : []),
        ]}
        total={[t("Payable"), sar(r.amount)]}
      />
    </Card>
  );
}

function VacationCalc({
  e,
  records,
  rules,
  can,
}: {
  e: HrEmployee | null;
  records: import("../../domain/hrFile").HrRecord[];
  rules: HrRules;
  can: boolean;
}) {
  const fromFile = e ? vacationOf(e, records, rules, new Date()) : null;
  const [hired, setHired] = useState(e?.hiredOn ?? "2021-01-01");
  const [on, setOn] = useState(todayIso());
  const [taken, setTaken] = useState(fromFile ? String(fromFile.taken) : "0");
  const [wage, setWage] = useState(e && can ? String(grossPay(e)) : "10000");
  const v = vacation({
    hiredOn: hired,
    on: new Date(`${on}T00:00:00Z`),
    takenDays: Number(taken) || 0,
    monthlyWage: Number(wage) || 0,
    rules,
  });
  return (
    <Card
      title={t("Vacation balance")}
      sub={t("Art. 109: {a} days a year, {b} after {y} years. Paid out on leaving (art. 111).", {
        a: rules.leave.days,
        b: rules.leave.daysAfter,
        y: rules.leave.afterYears,
      })}
      testId="hr-calc-vacation"
    >
      <div className={s.form}>
        <Field label="Hired on">
          <Input type="date" value={hired} onChange={(ev) => setHired(ev.target.value)} />
        </Field>
        <Field label="Balance on">
          <Input type="date" value={on} onChange={(ev) => setOn(ev.target.value)} />
        </Field>
        <Field label="Days of annual leave already taken">
          <Input
            value={taken}
            onChange={(ev) => setTaken(ev.target.value)}
            inputMode="decimal"
            data-testid="hr-calc-taken"
          />
        </Field>
        <Field label="Monthly salary (SAR)">
          <Input value={wage} onChange={(ev) => setWage(ev.target.value)} inputMode="decimal" />
        </Field>
      </div>
      {fromFile && <p className={s.note}>{t("Taken days filled from the approved annual leave in the file.")}</p>}
      <Result
        testId="hr-calc-vacation-balance"
        rows={[
          [t("Service"), t("{n} years", { n: v.serviceYears })],
          [t("Earned since hiring"), t("{n} days", { n: v.accrued })],
          [t("Taken"), t("{n} days", { n: v.taken })],
          [t("A day's wage (a month ÷ 30)"), sar(v.dailyWage)],
          [t("Worth if paid out"), sar(v.value)],
        ]}
        total={[t("Days left"), String(v.balance)]}
      />
    </Card>
  );
}

function SickCalc({
  e,
  records,
  can,
}: {
  e: HrEmployee | null;
  records: import("../../domain/hrFile").HrRecord[];
  can: boolean;
}) {
  const used = e ? sickLeave(records, new Date()).usedDays : 0;
  const [before, setBefore] = useState(String(used));
  const [days, setDays] = useState("10");
  const [wage, setWage] = useState(e && can ? String(grossPay(e)) : "10000");
  const r = sickPay(Number(before) || 0, Number(days) || 0, Number(wage) || 0);
  return (
    <Card
      title={t("Sick-leave pay")}
      sub={t("Art. 117, per 12 months: 30 days full pay, 60 at ¾ pay, 30 unpaid.")}
      testId="hr-calc-sick"
    >
      <div className={s.form}>
        <Field label="Sick days already used this year">
          <Input value={before} onChange={(ev) => setBefore(ev.target.value)} inputMode="numeric" />
        </Field>
        <Field label="Days of this sick leave">
          <Input
            value={days}
            onChange={(ev) => setDays(ev.target.value)}
            inputMode="numeric"
            data-testid="hr-calc-sickdays"
          />
        </Field>
        <Field label="Monthly salary (SAR)">
          <Input value={wage} onChange={(ev) => setWage(ev.target.value)} inputMode="decimal" />
        </Field>
      </div>
      <Result
        testId="hr-calc-sick-pay"
        rows={[
          [t("Days at full pay"), String(r.full)],
          [t("Days at three quarters"), String(r.three)],
          [t("Days unpaid"), String(r.unpaid)],
          [t("A day's wage (a month ÷ 30)"), sar(r.daily)],
        ]}
        total={[t("Paid for this sick leave"), sar(r.pay)]}
      />
    </Card>
  );
}

function FinalCalc({
  e,
  records,
  rules,
  can,
}: {
  e: HrEmployee | null;
  records: import("../../domain/hrFile").HrRecord[];
  rules: HrRules;
  can: boolean;
}) {
  const [wage, setWage] = useState(e && can ? String(eosbWage(e, rules)) : "10000");
  const [gross, setGross] = useState(e && can ? String(grossPay(e)) : "10000");
  const [y, setY] = useState(years(e));
  const [reason, setReason] = useState<LeaveReason>("resignation");
  const [balance, setBalance] = useState(
    e ? String(Math.floor(vacationOf(e, records, rules, new Date()).balance)) : "10",
  );
  const [unpaid, setUnpaid] = useState(String(new Date().getUTCDate()));
  const r = finalSettlement({
    wage: Number(wage) || 0,
    gross: Number(gross) || 0,
    years: Number(y) || 0,
    reason,
    leaveBalance: Number(balance) || 0,
    unpaidDays: Number(unpaid) || 0,
    rules,
  });
  return (
    <Card
      title={t("Final settlement")}
      sub={t(
        "Paid on leaving: end of service, unused leave, and unpaid days.",
      )}
      testId="hr-calc-final"
    >
      <div className={s.form}>
        <Field label="Monthly wage for end of service (SAR)">
          <Input value={wage} onChange={(ev) => setWage(ev.target.value)} inputMode="decimal" />
        </Field>
        <Field label="Monthly salary (SAR)">
          <Input value={gross} onChange={(ev) => setGross(ev.target.value)} inputMode="decimal" />
        </Field>
        <Field label="Years of service">
          <Input value={y} onChange={(ev) => setY(ev.target.value)} inputMode="decimal" />
        </Field>
        <Field label="How the service ends">
          <Select value={reason} onChange={(ev) => setReason(ev.target.value as LeaveReason)}>
            <option value="termination">{t("Ended by the employer")}</option>
            <option value="contract_end">{t("Fixed-term contract ended")}</option>
            <option value="resignation">{t("Resignation")}</option>
          </Select>
        </Field>
        <Field label="Unused annual leave (days)">
          <Input value={balance} onChange={(ev) => setBalance(ev.target.value)} inputMode="decimal" />
        </Field>
        <Field label="Days of the last month not yet paid">
          <Input value={unpaid} onChange={(ev) => setUnpaid(ev.target.value)} inputMode="numeric" />
        </Field>
      </div>
      <Result
        testId="hr-calc-final-total"
        rows={[
          [t("End-of-service award"), sar(r.award.amount)],
          [t("Unused leave"), sar(r.leave)],
          [t("Salary for the last days worked"), sar(r.salary)],
        ]}
        total={[t("Final settlement"), sar(r.total)]}
      />
      <p className={s.note}>
        {t(
          "Loans, advances, and deductions aren't included; subtract them. Pay within a week of leaving, or two weeks after resigning (art. 88).",
        )}{" "}
        {e ? `${t("Hired on")} ${niceDate(e.hiredOn)}.` : ""}
      </p>
    </Card>
  );
}
